import React, {useState} from "react";
import {Container, Row, Col, Card, Button, Modal} from "react-bootstrap";
import {
  FaPlus,
  FaClipboardList,
  FaUserPlus,
  FaComments,
  FaClock,
  FaSave,
  FaCheckCircle,
  FaEye,
  FaReply,
  FaTrash,
  FaExclamationCircle,
  FaUsers,
  FaArchive,
  FaBullhorn,
} from "react-icons/fa";
import {Link} from "react-router-dom";
import "./OperatorHub.css"; // Importaremos un CSS para el estilo Kanban

const ActionCard = ({icon, title, text, to, variant = "primary", onClick}) => (
  <Card className="h-100 shadow-sm text-center border-0 rounded-4">
    <Card.Body className="p-4">
      <div className={`fs-1 text-${variant} mb-3`}>{icon}</div>
      <Card.Title className="fw-bold">{title}</Card.Title>
      <Card.Text className="text-muted">{text}</Card.Text>
      <Button as={Link} to={to} variant={variant} className="rounded-pill px-4">
        Acceder
      </Button>
    </Card.Body>
  </Card>
);

// --- Componentes para el Tablero Kanban ---
const CampaignCard = ({campaign, lists, contacts, onDeleteClick}) => {
  const statusConfig = {
    Borrador: {
      color: "secondary",
      actionText: "Continuar Edición",
      link: `/communications/edit/${campaign.id}`,
    },
    Programada: {
      color: "primary",
      actionText: "Ver / Editar",
      link: `/communications/edit/${campaign.id}`,
    },
    Enviada: {
      color: "success",
      actionText: "Ver Detalles",
      link: `/communications/${campaign.id}`,
    },
  };

  const config = statusConfig[campaign.status] || statusConfig.Borrador;

  // --- Lógica de Métricas para tarjetas "Enviadas" ---
  const metrics = React.useMemo(() => {
    if (campaign.status !== "Enviada") return null;

    const recipientIds = new Set();
    campaign.sentToListIds.forEach((listId) => {
      const list = lists.find((l) => l.id === listId);
      list?.members.forEach((memberId) => recipientIds.add(memberId));
    });

    const totalSent = recipientIds.size;
    // Simulación de métricas (en una app real, esto vendría del backend)
    const readCount = Math.floor(
      totalSent * (Math.random() * (0.95 - 0.7) + 0.7)
    ); // Entre 70% y 95%
    const replyCount = Math.floor(readCount * (Math.random() * 0.3)); // Hasta 30% de los leídos responden
    const readRate =
      totalSent > 0 ? Math.round((readCount / totalSent) * 100) : 0;

    return {totalSent, readRate, replyCount};
  }, [campaign, lists]);

  // --- Lógica para info de Borradores y Programadas ---
  const targetInfo = React.useMemo(() => {
    if (campaign.status === "Enviada") return null;

    const recipientIds = new Set();
    campaign.sentToListIds.forEach((listId) => {
      const list = lists.find((l) => l.id === listId);
      list?.members.forEach((memberId) => recipientIds.add(memberId));
    });

    const listNames = campaign.sentToListIds
      .map((id) => lists.find((l) => l.id === id)?.name)
      .filter(Boolean);

    return {recipientCount: recipientIds.size, listNames};
  }, [campaign, lists]);

  return (
    <Card className={`kanban-card kanban-card-${config.color} mb-3 shadow-sm`}>
      <Card.Body>
        <Card.Title className="fw-bold fs-6">{campaign.name}</Card.Title>
        <Card.Text className="small text-muted">
          {campaign.status === "Borrador" && `Última edición: hace poco`}
        </Card.Text>

        {/* Info para Borradores y Programadas */}
        {(campaign.status === "Borrador" || campaign.status === "Programada") &&
          targetInfo && (
            <div className="kanban-card-details mb-3">
              <div className="small text-muted">
                <strong>Dirigido a:</strong>{" "}
                {targetInfo.listNames.join(", ") || "Ninguna lista"}
              </div>
              <div className="small text-muted d-flex align-items-center mt-1">
                <FaUsers className="me-2" />
                <strong>{targetInfo.recipientCount}</strong> destinatarios
                únicos.
              </div>
            </div>
          )}

        {/* Métricas para campañas enviadas */}
        {campaign.status === "Enviada" && metrics && (
          <div className="mb-3">
            <div className="d-flex justify-content-around text-center">
              <div>
                <div className="fw-bold fs-5">{metrics.totalSent}</div>
                <div className="small text-muted">Enviados</div>
              </div>
              <div>
                <div className="fw-bold fs-5">{metrics.readRate}%</div>
                <div className="small text-muted">
                  <FaEye className="me-1" /> Leídos
                </div>
              </div>
              <div>
                <div className="fw-bold fs-5">{metrics.replyCount}</div>
                <div className="small text-muted">
                  <FaReply className="me-1" /> Respuestas
                </div>
              </div>
            </div>
          </div>
        )}
      </Card.Body>
      <Card.Footer className="kanban-card-footer">
        <div className="d-flex justify-content-between align-items-center">
          <span className="small text-muted">
            {campaign.status === "Programada" &&
              `Programada: ${new Date(
                campaign.scheduledDate
              ).toLocaleString()}`}
            {campaign.status === "Enviada" &&
              `Enviada: ${new Date(campaign.sentDate).toLocaleString()}`}
          </span>
          <Link to={config.link}>
            <Button variant={`outline-${config.color}`} size="sm">
              {config.actionText}
            </Button>
          </Link>
          {campaign.status === "Borrador" && (
            <Button
              variant="outline-danger"
              size="sm"
              title="Eliminar Borrador"
              onClick={(e) => {
                e.preventDefault(); // Evita que el Link se active
                onDeleteClick(campaign);
              }}
              className="ms-2"
            >
              <FaTrash />
            </Button>
          )}
          {campaign.status === "Enviada" && (
            <Button
              variant="outline-secondary"
              size="sm"
              title="Archivar Campaña"
              onClick={(e) => {
                e.preventDefault();
                // onArchiveClick(campaign); // Lógica de archivar eliminada
              }}
              className="ms-2"
            >
              <FaArchive />
            </Button>
          )}
        </div>
      </Card.Footer>
    </Card>
  );
};

const OperatorHub = ({campaigns, setCampaigns, lists, contacts}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);

  // Lógica para clasificar campañas en columnas
  const drafts = campaigns.filter((c) => c.status === "Borrador");
  const scheduled = campaigns.filter((c) => c.status === "Programada");
  const sent = campaigns.filter((c) => c.status === "Enviada");

  const handleDeleteClick = (campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (campaignToDelete) {
      setCampaigns((prevCampaigns) =>
        prevCampaigns.filter((c) => c.id !== campaignToDelete.id)
      );
    }
    setShowDeleteModal(false);
    setCampaignToDelete(null);
  };

  return (
    <Container fluid className="p-4 bg-light">
      {/* Encabezado del Comunicaciones */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h2 className="fw-bold mb-1">
                <FaBullhorn className="me-3 text-primary" />
                Centro de Comunicaciones
              </h2>
              <p className="text-muted mb-0">
                Gestiona todas tus comunicaciones desde un solo lugar
              </p>
            </div>
            <Button
              as={Link}
              to="/communications/new"
              variant="primary"
              size="lg"
              className="rounded-pill px-4"
            >
              <FaPlus className="me-2" />
              Nueva Comunicación
            </Button>
          </div>
        </Col>
      </Row>

      {/* Fila de Accesos Rápidos */}
      <Row className="mb-4">
        <Col md={3}>
          <ActionCard
            icon={<FaUsers />}
            title="Gestionar Audiencias"
            text="Administra tus listas y segmentos de contactos."
            to="/audiences"
            variant="info"
          />
        </Col>
        <Col md={3}>
          <ActionCard
            icon={<FaUserPlus />}
            title="Añadir Contactos"
            text="Incorpora nuevos destinatarios a tu base."
            to="/contacts"
            variant="success"
          />
        </Col>
        <Col md={3}>
          <ActionCard
            icon={<FaComments />}
            title="Bandeja de Entrada"
            text="Revisa respuestas e interacciones."
            to="/inbox"
            variant="warning"
          />
        </Col>
        <Col md={3}>
          <ActionCard
            icon={<FaClipboardList />}
            title="Plantillas"
            text="Usa plantillas predefinidas para agilizar."
            to="/templates"
            variant="secondary"
          />
        </Col>
      </Row>

      {/* Tablero Kanban de Campañas */}
      <div className="kanban-board">
        {/* Columna Borradores */}
        <div className="kanban-column">
          <div className="kanban-column-header">
            <h5 className="fw-bold">
              <FaSave className="me-2" /> Borradores ({drafts.length})
            </h5>
          </div>
          <div className="kanban-column-content">
            {drafts.map((c) => (
              <CampaignCard
                key={c.id}
                campaign={c}
                lists={lists}
                contacts={contacts}
                onDeleteClick={handleDeleteClick}
              />
            ))}
          </div>
        </div>

        {/* Columna Programadas */}
        <div className="kanban-column">
          <div className="kanban-column-header">
            <h5 className="fw-bold">
              <FaClock className="me-2" /> Programadas ({scheduled.length})
            </h5>
          </div>
          <div className="kanban-column-content">
            {scheduled.map((c) => (
              <CampaignCard
                key={c.id}
                campaign={c}
                lists={lists}
                contacts={contacts}
                onDeleteClick={handleDeleteClick}
              />
            ))}
          </div>
        </div>

        {/* Columna Enviadas */}
        <div className="kanban-column">
          <div className="kanban-column-header">
            <h5 className="fw-bold text-success-emphasis">
              <FaCheckCircle className="me-2" /> Enviadas / En Curso (
              {sent.length})
            </h5>
          </div>
          <div className="kanban-column-content">
            {sent.map((c) => (
              <CampaignCard
                key={c.id}
                campaign={c}
                lists={lists}
                contacts={contacts}
                onDeleteClick={handleDeleteClick}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Modal de Confirmación para Eliminar */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExclamationCircle className="text-danger me-2" />
            Confirmar Eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de que quieres eliminar el borrador de la comunicación "
          <strong>{campaignToDelete?.name}</strong>"? Esta acción no se puede
          deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Sí, Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default OperatorHub;
